/* eslint-disable max-lines */

import type { IMdmOsUpdatePlatform } from '@/types/mdm/common'

function textDocumentVersionsUrl(id: number): string {
  return `/api/compliance/text-documents/${id}/versions`
}

export const ENDPOINTS = {
  HOME: {
    EXECUTIVE_REPORT: '/api/home/executive-report',
  },
  COMPLIANCE: {
    TASKS: {
      LIST: '/api/compliance/tasks',
      CREATE: '/api/compliance/tasks',
      GET: (id: number) => `/api/compliance/tasks/${id}`,
      UPDATE: (id: number) => `/api/compliance/tasks/${id}`,
      DELETE: (id: number) => `/api/compliance/tasks/${id}`,
      BULK_OWNER: '/api/compliance/tasks/bulk-owner',
      BULK_STATUS: '/api/compliance/tasks/bulk-status',
      BULK_CREATE: '/api/compliance/tasks/bulk',
    },
    RISKS: {
      LIST: '/api/compliance/risks',
      CREATE: '/api/compliance/risks',
      GET: (id: number) => `/api/compliance/risks/${id}`,
      UPDATE: (id: number) => `/api/compliance/risks/${id}`,
      DELETE: (id: number) => `/api/compliance/risks/${id}`,
      HISTORY: (id: number) => `/api/compliance/risks/${id}/history`,
      BULK_OWNER: '/api/compliance/risks/bulk-owner',
      BULK_STATUS: '/api/compliance/risks/bulk-status',
      BULK_DELETE: '/api/compliance/risks/bulk-delete',
      BULK_CREATE: '/api/compliance/risks/bulk',
      BULK_VALIDATE: '/api/compliance/risks/bulk/validate',
      CREATE_FROM_CATALOGUE: '/api/compliance/risks/catalogue',
      TREATMENT_PLANS: {
        CREATE: '/api/compliance/risks/treatment-plans',
        UPDATE: (id: string) => `/api/compliance/risks/treatment-plans/${id}`,
        DELETE: (id: string) => `/api/compliance/risks/treatment-plans/${id}`,
        BULK_CREATE: '/api/compliance/risks/treatment-plans/bulk',
      },
    },
    DOCUMENTS: {
      LIST: '/api/compliance/documents',
      GET: (id: number) => `/api/compliance/documents/${id}`,
      UPDATE: (id: number) => `/api/compliance/documents/${id}`,
      DELETE_CONTENT: (id: number) => `/api/compliance/documents/${id}/content`,
      UPLOAD_CONTENT: (id: number) => `/api/compliance/documents/${id}/content`,
      BULK_OWNER: '/api/compliance/documents/bulk-owner',
      BULK_STATUS: '/api/compliance/documents/bulk-status',
      SHARED_LINKS: (id: number) => `/api/compliance/documents/${id}/shared-links`,
    },
    TEXT_DOCUMENTS: {
      LIST: '/api/compliance/text-documents',
      GET: (id: number) => `/api/compliance/text-documents/${id}`,
      UPDATE: (id: number) => `/api/compliance/text-documents/${id}`,
      MENTIONS: (id: number) => `/api/compliance/text-documents/${id}/mentions`,
      VERSIONS: {
        LIST: textDocumentVersionsUrl,
        CREATE: textDocumentVersionsUrl,
        APPROVE: (id: number, versionId: number) => `${textDocumentVersionsUrl(id)}/${versionId}/approve`,
        COMPLETE: (id: number, versionId: number) => `${textDocumentVersionsUrl(id)}/${versionId}/complete`,
        REOPEN: (id: number, versionId: number) => `${textDocumentVersionsUrl(id)}/${versionId}/reopen`,
      },
    },
    CONTROLS: {
      LIST: '/api/compliance/controls',
      GET: (id: number) => `/api/compliance/controls/${id}`,
      UPDATE: (id: number) => `/api/compliance/controls/${id}`,
      DOCUMENTS_EVIDENCE: (id: number) => `/api/compliance/controls/${id}/documents-evidence`,
      BULK_OWNER: '/api/compliance/controls/bulk-owner',
      BULK_STATUS: '/api/compliance/controls/bulk-status',
    },
    NON_CONFORMITIES: {
      LIST: '/api/compliance/non-conformities',
      CREATE: '/api/compliance/non-conformities',
      GET: (id: number) => `/api/compliance/non-conformities/${id}`,
      UPDATE: (id: number) => `/api/compliance/non-conformities/${id}`,
      DELETE: (id: number) => `/api/compliance/non-conformities/${id}`,
      BULK_CREATE: '/api/compliance/non-conformities/bulk',
      BULK_VALIDATE: '/api/compliance/non-conformities/bulk/validate',
    },
  },
  EVIDENCES: {
    LIST: '/api/evidences',
    GET: (id: number) => `/api/evidences/${id}`,
    UPDATE: (id: number) => `/api/evidences/${id}`,
    BULK_OWNER: '/api/evidences/bulk-owner',
    BULK_STATUS: '/api/evidences/bulk-status',
  },
  APPROVED_SOFTWARE: {
    LIST: '/api/approved-software',
    CREATE: '/api/approved-software',
    UPDATE: (id: number) => `/api/approved-software/${id}`,
    DELETE: (id: number) => `/api/approved-software/${id}`,
    VENDORS: '/api/approved-software/vendors',
  },
  CONNECTORS: {
    LIST: '/api/connectors',
    STATUS: (id: string) => `/api/connectors/${id}/status`,
  },
  CLOUD_SECURITY: {
    DASHBOARD: '/api/cloud-security/dashboard',
    FINDINGS: '/api/cloud-security/findings',
    FINDING_DETAIL: (occurrenceId: number | string) => `/api/cloud-security/findings/${occurrenceId}`,
    MUTE_FINDINGS: '/api/cloud-security/findings/mute',
    UNMUTE_FINDINGS: '/api/cloud-security/findings/unmute',
  },
  APPSEC: {
    DASHBOARD: '/api/appsec/dashboard',
    SAST: '/api/appsec/sast',
    MUTE_SAST: '/api/appsec/sast/mute',
    UNMUTE_SAST: '/api/appsec/sast/unmute',
    SCA: '/api/appsec/sca',
    SBOM: '/api/appsec/sbom',
    SECRETS: '/api/appsec/secrets',
    MUTE_SECRETS: '/api/appsec/secrets/mute',
    UNMUTE_SECRETS: '/api/appsec/secrets/unmute',
    MUTE_SCA: '/api/appsec/sca/mute',
    UNMUTE_SCA: '/api/appsec/sca/unmute',
    CONTAINERS: '/api/appsec/containers',
    MUTE_CONTAINERS: '/api/appsec/containers/mute',
    UNMUTE_CONTAINERS: '/api/appsec/containers/unmute',
    DAST: '/api/appsec/dast',
    MUTE_DAST: '/api/appsec/dast/mute',
    UNMUTE_DAST: '/api/appsec/dast/unmute',
  },
  CRISIS_MANAGER: {
    GET: '/api/crisis-manager',
  },
  SIEM: {
    DASHBOARD: '/api/siem/dashboard',
    EVENT_HITS: '/api/siem/event-hits',
    CLOSED_ALERTS: '/api/siem/closed-alerts',
  },
  EDR: {
    ENDPOINT_ALERTS: '/api/edr/endpoint-alerts',
    ENDPOINT_RESPONSES: '/api/edr/endpoint-responses',
    ENDPOINT_RESTART: '/api/edr/endpoint/restart',
    ENDPOINT_ISOLATE: '/api/edr/endpoint/isolate',
    ENDPOINT_RESTORE: '/api/edr/endpoint/restore',
  },
  CASES: {
    CREATE: '/api/cases',
  },
  INFRASTRUCTURE: {
    VULNERABILITIES: '/api/infrastructure/vulnerabilities',
    MUTE_VULNERABILITIES: '/api/infrastructure/vulnerabilities/mute',
    UNMUTE_VULNERABILITIES: '/api/infrastructure/vulnerabilities/unmute',
    ALERTS: '/api/infrastructure/alerts',
    ALERTS_CLOSING_REASONS: '/api/infrastructure/alerts/closing-reasons',
    ALERT_UPDATE: (id: number) => `/api/infrastructure/alerts/${id}`,
    MONITORING: '/api/infrastructure/monitoring',
    MONITORING_AGENT_DOWNLOAD: '/api/infrastructure/monitoring/agent-download',
    ASSETS_SERVERS: '/api/infrastructure/assets/servers',
    ASSETS_LAPTOPS: '/api/infrastructure/assets/laptops',
    ASSETS_APPLICATION_UPDATE: (id: number) => `/api/infrastructure/assets/applications/${id}`,
    ASSET_GET: (id: number) => `/api/infrastructure/assets/${id}`,
    ASSET_APPLICATIONS: (id: number) => `/api/infrastructure/assets/${id}/applications`,
  },
  INTEGRATIONS: {
    AWS: '/api/integrations/aws',
    GCP: '/api/integrations/gcp',
    GCP_UPLOAD: '/api/integrations/gcp/upload',
    GITHUB: '/api/integrations/github',
    NOTION_ORG_TOKEN: '/api/integrations/notion/org-token',
    SLACK_ORG_TOKEN: '/api/integrations/slack/org-token',
  },
  EASM: {
    DASHBOARD: '/api/easm/dashboard',
    ASSETS: '/api/easm/assets',
    CERTIFICATES: '/api/easm/certificates',
    VULNERABILITIES: '/api/easm/vulnerabilities',
    MUTE_VULNERABILITIES: '/api/easm/vulnerabilities/mute',
    UNMUTE_VULNERABILITIES: '/api/easm/vulnerabilities/unmute',
    BREACHES: '/api/easm/breaches',
    MUTE_BREACHES: '/api/easm/breaches/mute',
  },
  QUESTIONNAIRES: {
    LIST: '/api/questionnaires',
    CREATE: '/api/questionnaires',
    GET: (id: number) => `/api/questionnaires/${id}`,
    DELETE: (id: number) => `/api/questionnaires/${id}`,
    DOWNLOAD_ORIGINAL: (id: number) => `/api/questionnaires/${id}/download-original`,
    DOWNLOAD_RESPONSE: (id: number) => `/api/questionnaires/${id}/download-response`,
    DOWNLOAD_ANSWERS: (id: number) => `/api/questionnaires/${id}/download-answers`,
    UNKNOWN_DOWNLOAD_RESPONSE: '/api/questionnaires/unknown/download-response',
    UNKNOWN_DOWNLOAD_ANSWERS: '/api/questionnaires/unknown/download-answers',
    IMPORT_QUESTIONS: (id: number) => `/api/questionnaires/${id}/import-questions`,
    UPLOAD_RESPONSE: (id: number) => `/api/questionnaires/${id}/upload-response`,
    PUBLISH_RESPONSE: (id: number) => `/api/questionnaires/${id}/publish-response`,
    COMPLETE: (id: number) => `/api/questionnaires/${id}/complete`,
    REBUILD_PACKAGE: (id: number) => `/api/questionnaires/${id}/rebuild-package`,
    DOWNLOAD_PACKAGE: (id: number) => `/api/questionnaires/${id}/download-package`,
    UNKNOWN_COMPLETE: '/api/questionnaires/unknown/complete',
    UNKNOWN_DOWNLOAD_PACKAGE: '/api/questionnaires/unknown/download-package',
  },
  CONTEXT_DOCUMENTS: {
    LIST: '/api/context-documents',
    CREATE: '/api/context-documents',
    OWNERS: '/api/context-documents/owners',
    ITEM: (id: string) => `/api/context-documents/${id}`,
    FILE: (id: string) => `/api/context-documents/${id}/file`,
    REVIEW: (id: string) => `/api/context-documents/${id}/review`,
    DOWNLOAD: (id: string) => `/api/context-documents/${id}/download`,
  },
  SUPPLIERS: {
    LIST: '/api/suppliers',
    GET: (id: number) => `/api/suppliers/${id}`,
    CREATE: '/api/suppliers',
    UPDATE: (id: number) => `/api/suppliers/${id}`,
    UPLOAD: '/api/suppliers/upload',
    DELETE: (id: number) => `/api/suppliers/${id}`,
    EXPORT: '/api/suppliers/export',
  },
  DEPARTMENTS: {
    LIST: '/api/departments',
    GET: (id: number) => `/api/departments/${id}`,
    CREATE: '/api/departments',
    UPDATE: (id: number) => `/api/departments/${id}`,
    DELETE: (id: number) => `/api/departments/${id}`,
  },
  IT_INVENTORY: {
    HARDWARE_CREATE: '/api/it-inventory/hardware',
    HARDWARE_BULK_CREATE: '/api/it-inventory/hardware/bulk',
    HARDWARE_UPDATE: (id: number) => `/api/it-inventory/hardware/${id}`,
    HARDWARE_DELETE: (id: number) => `/api/it-inventory/hardware/${id}`,
    CLOUD_RESOURCE_UPDATE: (id: number) => `/api/it-inventory/cloud-resources/${id}`,
  },
  REVIEW_ENTRIES: {
    CREATE: '/api/review-entries',
    FILE: (id: number) => `/api/review-entries/${id}/file`,
  },
  ASSET_MANAGER: {
    LIST: '/api/asset-manager',
    GET: (id: number) => `/api/asset-manager/${id}`,
    CREATE: '/api/asset-manager',
    BULK_CREATE: '/api/asset-manager/bulk',
    UPDATE: (id: number) => `/api/asset-manager/${id}`,
    DELETE: (id: number) => `/api/asset-manager/${id}`,
  },
  EMPLOYEES: {
    LIST: '/api/people/employees',
    CREATE: '/api/people/employees',
    GET: (id: string) => `/api/people/employees/${id}`,
    UPDATE_TRAINING: (id: string) => `/api/people/employees/${id}`,
    BULK_UPLOAD: '/api/people/employees/bulk-upload',
    ONBOARDING_TASKS: (id: string) => `/api/people/employees/${id}/onboarding-tasks`,
    OFFBOARDING_TASKS: (id: string) => `/api/people/employees/${id}/offboarding-tasks`,
  },
  PEOPLE: {
    EMPLOYEES: {
      LIST: '/api/people/employees',
      CREATE: '/api/people/employees',
      GET: (id: string) => `/api/people/employees/${id}`,
      UPDATE_TRAINING: (id: string) => `/api/people/employees/${id}`,
      BULK_UPLOAD: '/api/people/employees/bulk-upload',
      ONBOARDING_TASKS: (id: string) => `/api/people/employees/${id}/onboarding-tasks`,
      OFFBOARDING_TASKS: (id: string) => `/api/people/employees/${id}/offboarding-tasks`,
      DEPARTMENTS: (id: string) => `/api/people/employees/${id}/departments`,
      SIGNED_POLICIES: (id: string) => `/api/people/employees/${id}/signed-policies`,
      TASK_EVIDENCE_LIST: (employeeId: string, taskId: number) =>
        `/api/people/employees/${employeeId}/tasks/${taskId}/evidence`,
      TASK_EVIDENCE_LINKS: (employeeId: string, taskId: number) =>
        `/api/people/employees/${employeeId}/tasks/${taskId}/evidence/links`,
      TASK_EVIDENCE_LINK: (employeeId: string, taskId: number, linkId: number) =>
        `/api/people/employees/${employeeId}/tasks/${taskId}/evidence/links/${linkId}`,
      TASK_EVIDENCE_FILES: (employeeId: string, taskId: number) =>
        `/api/people/employees/${employeeId}/tasks/${taskId}/evidence/files`,
      TASK_EVIDENCE_FILE: (employeeId: string, taskId: number, fileId: number) =>
        `/api/people/employees/${employeeId}/tasks/${taskId}/evidence/files/${fileId}`,
    },
    CHECKLIST_TEMPLATES: {
      LIST: '/api/people/checklist-templates',
      CREATE: '/api/people/checklist-templates',
      UPDATE: (id: number) => `/api/people/checklist-templates/${id}`,
      DELETE: (id: number) => `/api/people/checklist-templates/${id}`,
      REORDER: '/api/people/checklist-templates/reorder',
      RESET: '/api/people/checklist-templates/reset',
    },
    PHISHING: {
      CAMPAIGNS: '/api/people/phishing/campaigns',
      CAMPAIGN: (id: string) => `/api/people/phishing/campaigns/${id}`,
    },
    TRAINING: '/api/people/training',
    IDENTITIES: {
      LIST: '/api/people/identities',
      SOURCE_OPTIONS: '/api/people/identities/source-options',
    },
    USERS: {
      LIST: '/api/people/users',
      CREATE: '/api/people/users',
      DELETE: (id: string) => `/api/people/users/${id}`,
      BULK_DELETE: '/api/people/users/bulk-delete',
      UPDATE_ROLE: (id: string) => `/api/people/users/${id}`,
      PARSE_CSV: '/api/people/users/parse-csv',
      SWITCH_TENANT: '/api/people/users/switch-tenant',
    },
    SERVICE_ACCOUNTS: {
      LIST: '/api/people/service-accounts',
      MARK: '/api/people/service-accounts',
      RESTORE: (id: string) => `/api/people/service-accounts/${id}/restore`,
    },
  },
  POLICIES: {
    LIST: '/api/policies',
    CONTENT: (documentId: number) => `/api/policies/${documentId}/content`,
    SIGN_POLICY: (documentId: number) => `/api/policies/${documentId}/sign`,
    SIGNATURES_EXPORT: '/api/policies/signatures/export',
  },
  CHAT: { MESSAGE: '/api/chat' },
  ADMIN: {
    CHAT: {
      CONVERSATIONS: '/api/internal/admin/chat/conversations',
      CONVERSATION: (id: string) => `/api/internal/admin/chat/conversations/${id}`,
    },
  },
  AUTH: { SUPERADMIN: '/api/auth/superadmin' },
  CAPABILITIES: {
    PASSWORD_MANAGER_VAULT: '/api/capabilities/password-manager/vault-url',
    MONITORING_AGENT_DOWNLOAD: '/api/capabilities/monitoring/agent-download-url',
  },
  BACKOFFICE: {
    SECURITY_MANAGERS: '/api/backoffice/security-managers',
    SCANS: {
      REPOSITORIES: {
        LIST: '/api/backoffice/scans/repositories',
        CREATE: '/api/backoffice/scans/repositories',
        DELETE: (repositoryId: number) => `/api/backoffice/scans/repositories/${repositoryId}`,
      },
    },
    ORGANIZATIONS: {
      LINKED_DOCUMENTS: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/linked-documents`,
      LINKED_DOCUMENTS_LINK: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/linked-documents/link`,
      LINKED_DOCUMENTS_CLONE_REGISTERS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/linked-documents/clone-registers`,
      SEED_DEMO_DATA: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/seed-demo-data`,
      PHISHING_CAMPAIGNS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/phishing/campaigns`,
      PHISHING_CAMPAIGN_TEMPLATE: (organizationId: number, campaignId: number) =>
        `/api/backoffice/organizations/${organizationId}/phishing/campaigns/${campaignId}/template`,
      CANIPHISH_CAMPAIGNS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/phishing/caniphish-campaigns`,
      EMPLOYEE_CENTER_DOCUMENTS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/employee-center-documents`,
      EMPLOYEE_CENTER_DOCUMENT_DEPARTMENTS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/employee-center-documents/departments`,
      DEPARTMENTS: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/departments`,
      CREATE: '/api/backoffice/organizations',
      GET: (organizationId: number) => `/api/backoffice/organizations/${organizationId}`,
      UPDATE: (organizationId: number) => `/api/backoffice/organizations/${organizationId}`,
      TIMELINE: (organizationId: number, standardId: number) =>
        `/api/backoffice/organizations/${organizationId}/timelines/${standardId}`,
      MDM: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/mdm`,
      MDM_LIBRARY: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/mdm/library`,
      MDM_LIBRARY_PROFILE: (organizationId: number, profileId: string) =>
        `/api/backoffice/organizations/${organizationId}/mdm/library/${profileId}`,
      TRUST_CENTER: (organizationId: number) => `/api/backoffice/organizations/${organizationId}/trust-center`,
      TRUST_CENTER_STANDARDS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/trust-center/standards`,
      TRUST_CENTER_SUBPROCESSORS: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/trust-center/subprocessors`,
      TRUST_CENTER_SUBPROCESSOR: (organizationId: number, id: number) =>
        `/api/backoffice/organizations/${organizationId}/trust-center/subprocessors/${id}`,
      TRUST_CENTER_RESOURCES: (organizationId: number) =>
        `/api/backoffice/organizations/${organizationId}/trust-center/resources`,
      TRUST_CENTER_RESOURCE: (organizationId: number, id: number) =>
        `/api/backoffice/organizations/${organizationId}/trust-center/resources/${id}`,
    },
    COMPLIANCE_DATA: {
      CLUSTERS: '/api/backoffice/compliance-data/clusters',
      STANDARDS: '/api/backoffice/compliance-data/standards',
      DOCUMENTS: '/api/backoffice/compliance-data/documents',
      LINKED_DOCUMENTS: '/api/backoffice/compliance-data/linked-documents',
      PROCESS_TRANSCRIPT: '/api/backoffice/compliance-data/process-transcript',
    },
    STANDARD_EDITOR: {
      CONTROLS: '/api/backoffice/standard-editor/controls',
      DOCUMENTS: '/api/backoffice/standard-editor/documents',
      EVIDENCES: '/api/backoffice/standard-editor/evidences',
      CONTROL_PREVIEW_DELETE: (controlId: number) =>
        `/api/backoffice/standard-editor/controls/${controlId}/preview-delete`,
      DOCUMENT_PREVIEW_DELETE: (documentId: number) =>
        `/api/backoffice/standard-editor/documents/${documentId}/preview-delete`,
      DOCUMENT_DELETE: (documentId: number) => `/api/backoffice/standard-editor/documents/${documentId}`,
      EVIDENCE_PREVIEW_DELETE: (evidenceId: number) =>
        `/api/backoffice/standard-editor/evidences/${evidenceId}/preview-delete`,
      EVIDENCE_DELETE: (evidenceId: number) => `/api/backoffice/standard-editor/evidences/${evidenceId}`,
      EVIDENCE_CREATE: '/api/backoffice/standard-editor/evidences',
      EVIDENCE_UPDATE: (evidenceId: number) => `/api/backoffice/standard-editor/evidences/${evidenceId}`,
    },
  },
  MDM: {
    GROUPS: {
      LIST: '/api/mdm/groups',
      CREATE: '/api/mdm/groups',
      GET: (id: number) => `/api/mdm/groups/${id}`,
      UPDATE: (id: number) => `/api/mdm/groups/${id}`,
      DELETE: (id: number) => `/api/mdm/groups/${id}`,
    },
    HOSTS: {
      LIST: '/api/mdm/hosts',
      STATUSES: '/api/mdm/hosts/statuses',
      APPLICATIONS: (id: number) => `/api/mdm/hosts/${id}/applications`,
      LOCK: (id: number) => `/api/mdm/hosts/${id}/lock`,
      UNLOCK: (id: number) => `/api/mdm/hosts/${id}/unlock`,
      WIPE: (id: number) => `/api/mdm/hosts/${id}/wipe`,
      NOTIFY_DISK_ENCRYPTION: (id: number) => `/api/mdm/hosts/${id}/disk-encryption/notify`,
    },
    SOFTWARE: {
      LIST: '/api/mdm/software',
      CREATE: '/api/mdm/software',
      INSTALL: (id: string) => `/api/mdm/software/${id}/install`,
      UNINSTALL: (id: string) => `/api/mdm/software/${id}/uninstall`,
      HOSTS: (id: string) => `/api/mdm/software/${id}/hosts`,
      HOST_INSTALL_RESULT: (id: string, hostId: number) => `/api/mdm/software/${id}/hosts/${hostId}/install-result`,
      INSTALL_STATUS: '/api/mdm/software/install-status',
      APPROVE: '/api/mdm/software/approve',
    },
    SCRIPTS: {
      LIST: '/api/mdm/scripts',
      CREATE: '/api/mdm/scripts',
      GET: (id: string) => `/api/mdm/scripts/${id}`,
      UPDATE: (id: string) => `/api/mdm/scripts/${id}`,
      DELETE: (id: string) => `/api/mdm/scripts/${id}`,
      RUN: (id: string) => `/api/mdm/scripts/${id}/run`,
      RESULT: (executionId: string) => `/api/mdm/scripts/results/${executionId}`,
      BATCH_RESULTS: (batchExecutionId: string) => `/api/mdm/scripts/batch/${batchExecutionId}/results`,
    },
    POLICIES: {
      OS_UPDATES: {
        GET: '/api/mdm/policies/os-updates',
        UPDATE: (platform: IMdmOsUpdatePlatform) => `/api/mdm/policies/os-updates/${platform}`,
      },
      DISK_ENCRYPTION: {
        GET: '/api/mdm/policies/disk-encryption',
        UPDATE: '/api/mdm/policies/disk-encryption',
        OVERVIEW: '/api/mdm/policies/disk-encryption/overview',
      },
    },
    PROFILES: {
      LIST: '/api/mdm/profiles',
      CREATE: '/api/mdm/profiles',
      GET: (id: string) => `/api/mdm/profiles/${id}`,
      UPDATE: (id: string) => `/api/mdm/profiles/${id}`,
      DELETE: (id: string) => `/api/mdm/profiles/${id}`,
      DEPLOY: (id: string) => `/api/mdm/profiles/${id}/deploy`,
      DEPLOYMENT_HOSTS: (deploymentId: string) => `/api/mdm/profiles/deployments/${deploymentId}/hosts`,
    },
  },
} as const
